"use client";

import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import { useThoughtCoCreate } from "@/contexts/ThoughtCoCreateContext";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@/contexts/UserContext";
import { apiRemote } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import { StyledLink } from "@/components/StyledLink";

const STATUS_DRAFT = process.env.STATUS_DRAFT;
const STATUS_SUBMITTED = process.env.STATUS_SUBMITTED;
const STATUS_NOT_STARTED = process.env.STATUS_NOT_STARTED;
const STATUS_IN_PROGRESS = process.env.STATUS_IN_PROGRESS;
const STATUS_ENDED = process.env.STATUS_ENDED;

export default function Deliverable1() {
  const [statement, setStatement] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { userRoundParts, loading, loadUserRound1 } = useThoughtCoCreate();
  const { user } = useUser();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const { id } = useParams<{ id: string }>();
  const alert = useAlert();
  const localStorageKey = `draft_deliverable1_${user?.user_login}_${id}`;
  useEffect(() => {
    const current_round = Number(id) - 1;
    if (loading || !userRoundParts?.[current_round]) return;

    const roundData = userRoundParts[current_round];
    if (roundData.submission_data) {
      const submissionData = JSON.parse(
        roundData.submission_data
      );
      setStatement(submissionData.problem);
      setWordCount(
        submissionData.problem.trim().split(/\s+/).filter(Boolean).length
      );
    }else{
      const savedStatement = localStorage.getItem(localStorageKey);
      if (savedStatement) {
        setStatement(savedStatement);
        setWordCount(savedStatement.trim().split(/\s+/).filter(Boolean).length);
      }
    }

    if (roundData.status == Number(STATUS_SUBMITTED)) {
      setIsSubmitted(true);
    } else {
      setIsSubmitted(false);
    }
    const dueDate = new Date(roundData.due_date);
    const today = new Date();

    // console.log(today, dueDate)
    const dueDateOnly = new Date(
      dueDate.getFullYear(),
      dueDate.getMonth(),
      dueDate.getDate()
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    // if (
    //   todayOnly < dueDateOnly &&
    //   roundData.status !== Number(STATUS_SUBMITTED)
    // ) {
    //   setStatusMessage(
    //     `Submission is not open yet. Due date: ${roundData.due_date}`
    //   );
    // } else
     if (
      todayOnly > dueDateOnly &&
      roundData.status !== Number(STATUS_SUBMITTED)
    ) {
      setStatusMessage(
        `Submission is overdue. Final deadline: ${roundData.due_date}`
      );
      setIsDeadlinePassed(true);
    } else if (
      todayOnly > dueDateOnly
    ) {
      setIsDeadlinePassed(true);
    } else {
      setStatusMessage(null); // Within the allowed time, no message needed
      setIsDeadlinePassed(false);
    }
  }, [id, userRoundParts, loading]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    setIsSubmitting(true);

    try {
      apiRemote
      .postStep({
        user: user?.user_login || "",
        round_part_id: Number(id),
        status: 2,
        submission_data: JSON.stringify({ problem: statement }),
        step: 1,
      })
      .then((data) => {
        console.log(data);
        if (data.code == 200) {
          alert.success(data.message);
          setIsSubmitted(true);
          loadUserRound1();
          localStorage.removeItem(localStorageKey);
        } else {
          alert.error(data.message);
        }
      }).finally(() => setIsSubmitting(false));
    } catch (error) {
      alert.error("Something was wrong!");
    }
    
    // Handle submission logic here
  };

  const handleSaveDraft = (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeadlinePassed || isSubmitting) return;

    setIsSubmitting(true);
    try {
      apiRemote
      .postStep({
        user: user?.user_login || "",
        round_part_id: Number(id),
        status: 1,
        submission_data: JSON.stringify({ problem: statement }),
        step: 1,
      })
      .then((data) => {
        if (data.code == 200) {
          alert.success(data.message);
          loadUserRound1();
          localStorage.removeItem(localStorageKey);
        } else {
          alert.error(data.message);
        }
      }).finally(() => setIsSubmitting(false));
    } catch (error) {
      alert.error("Something was wrong!");
    }
    // Handle submission logic here
  };

  const handleChange = (value: any) => {
    setStatement(value);
    setWordCount(value.trim().split(/\s+/).filter(Boolean).length);
    localStorage.setItem(localStorageKey, value);
  };

  const handleNext = () => {
    const nextId = Number(id) + 1;
    navigate(`../${nextId}`);
  };

  const handlePrevious = () => {
    const previousId = Number(id) - 1;
    navigate(`../${previousId}`);
  };

  return (
    <>
      {statusMessage ? (
        <Typography
          variant="body1"
          color={statusMessage.includes("overdue") ? "error" : "warning"}
          sx={{ mb: 2 }}
        >
          {statusMessage}
        </Typography>
      ) : (
        <>
          <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              p: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TextField
              multiline
              rows={10}
              placeholder="Enter your problem statement here..."
              value={statement}
              onChange={(e) => handleChange(e.target.value)}
              variant="outlined"
              fullWidth
              InputProps={{ readOnly: isDeadlinePassed }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px 4px 0 0",
                  "& fieldset": {
                    border: "none",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                  "&:hover fieldset": {
                    borderColor: "divider",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "divider",
                  },
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 2,
                py: 1,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Tooltip title="Additional information about the problem statement">
                <IconButton size="small" color="primary">
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ ml: "auto" }}
              >
                {wordCount}/300 words
              </Typography>
            </Box>
          </Paper>

          <Stack
            direction="row"
            spacing={2}
            sx={{
              mt: 3,
              justifyContent: "space-between",
            }}
          >
            <Button
              variant="text"
              color="inherit"
              sx={{ color: "text.primary" }}
            >
              <StyledLink to={`../../dashboard`} style={{ color: "#000" }}>
                Back to Dashboard
              </StyledLink>
            </Button>
            <Box>
              {isSubmitting && <CircularProgress size={20} sx={{ ml: 1, verticalAlign: "middle" }} />}
              {/* <Button
                variant="text"
                sx={{
                  mr: 2,
                  color: "text.primary",
                }}
                disabled={!statement || isSubmitting}
                onClick={handleSaveDraft}
              >
                Save as Draft
              </Button> */}
              <Button
                variant="contained"
                type="submit"
                sx={{
                  bgcolor: "black",
                  "&:hover": {
                    bgcolor: "grey.900",
                  },
                }}
                onClick={handleSubmit}
                disabled={!statement || isSubmitting || isDeadlinePassed}
              >
                Submit D1
              </Button>
              {isSubmitted && (
                <Button
                  variant="contained"
                  sx={{
                    ml: 2,
                    bgcolor: "blue",
                    "&:hover": {
                      bgcolor: "darkblue",
                    },
                  }}
                  onClick={handleNext}
                >
                  Next
                </Button>
              )}
            </Box>
          </Stack>
        </>
      )}
    </>
  );
}
